import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import getMediaElementByUuid from 'editor/src/store/design/selector/getMediaElementByUuid';
import { updatePersonalizationFieldGroupAction } from 'editor/src/store/design/slice';
import { ConditionChild, ConditionGroup, PersonalizationFieldGroup } from 'editor/src/store/design/types';
import {
  getPersonalizationFieldGroups,
  groupPersonalizationFields,
  personalizationFieldKey,
} from 'editor/src/store/design/util/personalizationFieldGroups';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';

import styles from './index.module.scss';

interface Props {
  conditionGroup: ConditionGroup;
  spreadIndex: number;
  childrenKey: string;
  elements: ConditionChild[];
  renderFields?: (elements: ConditionChild[]) => React.ReactNode;
}

function GroupName({ name, label, onChange }: { name: string; label: string; onChange: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  const finished = useRef(false);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  function finish(cancel = false) {
    if (finished.current) {
      return;
    }
    finished.current = true;
    const nextName = value.trim();
    if (!cancel && nextName && nextName !== name) {
      onChange(nextName);
    }
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={styles.nameInput}
        aria-label={label}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        onBlur={() => finish()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (!event.nativeEvent.isComposing && (event.key === 'Enter' || event.key === 'Escape')) {
            event.preventDefault();
            finish(event.key === 'Escape');
          }
        }}
      />
    );
  }
  return (
    <button
      type="button"
      className={styles.groupName}
      aria-label={`${label}: ${name}`}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        finished.current = false;
        setValue(name);
        setEditing(true);
      }}
    >
      {name}
    </button>
  );
}

function GroupActions({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div
      ref={ref}
      className={styles.groupActions}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setOpen(false);
          buttonRef.current?.focus();
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className={styles.moreButton}
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="3" cy="8" r="1" fill="currentColor" />
          <circle cx="8" cy="8" r="1" fill="currentColor" />
          <circle cx="13" cy="8" r="1" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div className={styles.actionPopover} onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

function GroupCard({
  group,
  conditionGroup,
  initiallyOpen,
  children,
}: {
  group: PersonalizationFieldGroup;
  conditionGroup: ConditionGroup;
  initiallyOpen: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(initiallyOpen);
  const selection = useSelector((state) => state.editor.selectedElementUuids.join(','));
  const memberKey = group.elements.map(personalizationFieldKey).join(',');
  useLayoutEffect(() => {
    const selected = new Set(String(selection).split(',').map(Number));
    const visited = new Set<string>();
    function containsSelection(fields: ConditionChild[]): boolean {
      return fields.some((field) => {
        if (field.type !== 'condition') {
          return selected.has(field.id);
        }
        if (visited.has(field.id)) {
          return false;
        }
        visited.add(field.id);
        return (
          conditionGroup.conditions[field.id]?.options.some((option) =>
            containsSelection(conditionGroup.children[`${field.id}-${option.id}`] || []),
          ) || false
        );
      });
    }
    if (containsSelection(group.elements)) {
      setExpanded(true);
    }
  }, [selection, memberKey]);
  return (
    <details className={styles.card} open={expanded} onToggle={(event) => setExpanded(event.currentTarget.open)}>
      {children}
    </details>
  );
}

function FieldGroupSettings({ conditionGroup, spreadIndex, childrenKey, elements, renderFields }: Props) {
  const tr = useTr();
  const dispatch = useDispatch();
  const [draft, setDraft] = useState<PersonalizationFieldGroup>();
  const [search, setSearch] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const names = useSelector(
    (state) =>
      elements.map((element) =>
        element.type === 'condition'
          ? conditionGroup.conditions[element.id]?.name
          : getMediaElementByUuid(state, element.id)?.name,
      ),
    shallowEqual,
  );
  const sections = groupPersonalizationFields(conditionGroup, elements, childrenKey);
  const groups = getPersonalizationFieldGroups(conditionGroup).filter((group) => group.childrenKey === childrenKey);

  function save(group: PersonalizationFieldGroup) {
    dispatch(saveUndoRedoStateOperation('Update field group'));
    dispatch(updatePersonalizationFieldGroupAction({ spreadIndex, group }));
    setDraft(undefined);
    setSearch('');
    setSelectedOnly(false);
  }

  function add() {
    setSearch('');
    setSelectedOnly(false);
    setDraft({ id: crypto.randomUUID(), name: '', childrenKey, elements: [] });
  }

  const selectedFields = elements.filter((element) =>
    draft?.elements.some((item) => personalizationFieldKey(item) === personalizationFieldKey(element)),
  );
  const fieldName = (element: ConditionChild) =>
    names[elements.indexOf(element)] || `${element.type} ${elements.indexOf(element) + 1}`;

  const visibleFields = elements.filter((element) =>
    selectedOnly
      ? selectedFields.includes(element)
      : fieldName(element).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );

  return (
    <section className={styles.settings}>
      <div className={styles.title}>{tr('editor-personalization-field-groups', 'Field groups')}</div>
      <p>
        {tr(
          'editor-personalization-field-groups-help',
          'Group related fields to display them side by side in a collapsible section.',
        )}
      </p>
      {!draft &&
        groups.map((group, index) => (
          <div key={group.id} className={styles.groupShell}>
            <GroupCard group={group} conditionGroup={conditionGroup} initiallyOpen={index === 0}>
              <summary className={styles.row}>
                <GroupName
                  name={group.name}
                  label={tr('editor-personalization-field-group-name', 'Group name')}
                  onChange={(name) => save({ ...group, name })}
                />
                <span className={styles.count}>{group.elements.length}</span>
              </summary>
              {renderFields?.(
                sections.flatMap((section) => (section.group && section.group.id === group.id ? section.elements : [])),
              )}
            </GroupCard>
            <GroupActions label={`${tr('editor-more-options', 'More options')}: ${group.name}`}>
              <Button
                secondary
                fullWidth
                onClick={() => {
                  setSearch('');
                  setSelectedOnly(false);
                  setDraft({ ...group, elements: [...group.elements] });
                }}
              >
                {tr('editor-edit', 'Edit')}
              </Button>
              <Button tertiary fullWidth onClick={() => save({ ...group, elements: [] })}>
                {tr('editor-remove', 'Remove')}
              </Button>
            </GroupActions>
          </div>
        ))}
      {!draft && (
        <Button secondary fullWidth className={styles.addButton} onClick={add}>
          {tr('editor-personalization-add-field-group', 'Add field group')}
        </Button>
      )}
      {draft && (
        <div className={styles.form}>
          <h3 className={styles.formTitle}>
            {groups.some((group) => group.id === draft.id)
              ? tr('editor-edit', 'Edit')
              : tr('editor-personalization-add-field-group', 'Add field group')}
          </h3>
          <label>
            {tr('editor-personalization-field-group-name', 'Group name')}
            <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          </label>

          <div
            className={styles.filters}
            role="group"
            aria-label={tr('editor-personalization-group-fields', 'Fields in this group')}
          >
            <button type="button" aria-pressed={!selectedOnly} onClick={() => setSelectedOnly(false)}>
              {tr('editor-personalization-all-fields', 'All fields')} <span>{elements.length}</span>
            </button>
            <button type="button" aria-pressed={selectedOnly} onClick={() => setSelectedOnly(true)}>
              {tr('editor-personalization-selected-fields', 'Selected')} <span>{selectedFields.length}</span>
            </button>
          </div>
          {!selectedOnly && (
            <label>
              {tr('editor-search', 'Search')}
              <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} />
            </label>
          )}
          <fieldset>
            <legend className={styles.srOnly}>
              {tr('editor-personalization-group-fields', 'Fields in this group')}
            </legend>
            {!visibleFields.length && <p>{tr('editor-no-results', 'No results')}</p>}
            {elements.map((element, index) => {
              if (!visibleFields.includes(element)) {
                return null;
              }
              const key = personalizationFieldKey(element);
              const owner = groups.find(
                (group) =>
                  group.id !== draft.id && group.elements.some((item) => personalizationFieldKey(item) === key),
              );
              const checked = draft.elements.some((item) => personalizationFieldKey(item) === key);
              return (
                <label key={key} className={styles.choice} data-selected={checked}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setDraft(
                        (current) =>
                          current && {
                            ...current,
                            elements: current.elements.some((item) => personalizationFieldKey(item) === key)
                              ? current.elements.filter((item) => personalizationFieldKey(item) !== key)
                              : [...current.elements, element],
                          },
                      )
                    }
                  />
                  <span>
                    {names[index] || `${element.type} ${index + 1}`}
                    {owner && <small className={styles.owner}>{owner.name}</small>}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <div className={styles.footer}>
            <Button
              className={styles.saveButton}
              fullWidth
              primary
              disabled={!draft.name.trim() || !draft.elements.length}
              onClick={() => save(draft)}
            >
              {tr('Save', 'Save')}
            </Button>
            <Button secondary fullWidth onClick={() => setDraft(undefined)}>
              {tr('editor-cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default FieldGroupSettings;
